import React, { useMemo, useState } from 'react';
import {
  Container,
  Card,
  Button,
  Badge,
  Form,
  InputGroup,
  Modal,
  Spinner,
  Alert,
  Row,
  Col,
  Table,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';
import {
  useGetTargetAccountsQuery,
  useCreateTargetAccountMutation,
  useUpdateTargetAccountMutation,
  useDeleteTargetAccountMutation,
  useGetClientsQuery,
  useGetUsersQuery,
} from '../services/api';
import { useUser } from '../contexts/UserContext';
import {
  Plus,
  Search,
  Pencil,
  Trash,
  Archive,
  ArrowCounterclockwise,
  Inbox,
  Link45deg,
  Globe2,
  Bullseye,
} from 'react-bootstrap-icons';

interface TargetAccount {
  id: string;
  companyName: string;
  website?: string;
  industry?: string;
  locationCount?: string;
  targetFit?: 'high' | 'medium' | 'low';
  priority?: 'P1' | 'P2' | 'P3';
  stage?: 'identified' | 'researching' | 'outreaching' | 'engaged' | 'converted' | 'dropped';
  status?: 'active' | 'archived';
  outreachAngle?: string;
  strategyNotes?: string;
  tags?: string[];
  linkedClientId?: string;
  linkedAccountId?: string;
  linkedLeadId?: string;
  ownerId?: string;
  visibility?: 'global' | 'personal';
  createdAt?: string;
  updatedAt?: string;
}

type EditableFormState = {
  id?: string;
  companyName: string;
  website: string;
  industry: string;
  locationCount: string;
  targetFit: '' | 'high' | 'medium' | 'low';
  priority: '' | 'P1' | 'P2' | 'P3';
  stage: 'identified' | 'researching' | 'outreaching' | 'engaged' | 'converted' | 'dropped';
  outreachAngle: string;
  strategyNotes: string;
  linkedClientId: string;
  visibility: 'global' | 'personal';
};

const EMPTY_FORM: EditableFormState = {
  companyName: '',
  website: '',
  industry: '',
  locationCount: '',
  targetFit: '',
  priority: '',
  stage: 'identified',
  outreachAngle: '',
  strategyNotes: '',
  linkedClientId: '',
  visibility: 'global',
};

const STAGE_LABELS: Record<EditableFormState['stage'], string> = {
  identified: 'Identified',
  researching: 'Researching',
  outreaching: 'Outreaching',
  engaged: 'Engaged',
  converted: 'Converted',
  dropped: 'Dropped',
};

const TARGET_FIT_LABELS: Record<NonNullable<TargetAccount['targetFit']>, string> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

function stageBadgeVariant(stage?: TargetAccount['stage']): string {
  switch (stage) {
    case 'identified':
      return 'secondary';
    case 'researching':
      return 'info';
    case 'outreaching':
      return 'primary';
    case 'engaged':
      return 'warning';
    case 'converted':
      return 'success';
    case 'dropped':
      return 'dark';
    default:
      return 'secondary';
  }
}

function targetFitBadgeVariant(fit?: TargetAccount['targetFit']): string {
  switch (fit) {
    case 'high':
      return 'success';
    case 'medium':
      return 'warning';
    case 'low':
      return 'secondary';
    default:
      return 'light';
  }
}

function priorityBadgeVariant(priority?: TargetAccount['priority']): string {
  switch (priority) {
    case 'P1':
      return 'danger';
    case 'P2':
      return 'warning';
    case 'P3':
      return 'secondary';
    default:
      return 'light';
  }
}

function normalizeWebsiteForHref(raw?: string): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

const STATUS_FILTERS: ReadonlyArray<{ key: 'active' | 'archived' | 'all'; label: string }> = [
  { key: 'active', label: 'Active' },
  { key: 'archived', label: 'Archived' },
  { key: 'all', label: 'All' },
];

const TargetAccounts: React.FC = () => {
  const { currentUser } = useUser() as { currentUser: any };
  const [statusFilter, setStatusFilter] = useState<'active' | 'archived' | 'all'>('active');
  const [searchQuery, setSearchQuery] = useState('');
  const [industryFilter, setIndustryFilter] = useState<string>('');
  const [ownerFilter, setOwnerFilter] = useState<string>('');
  const [stageFilter, setStageFilter] = useState<string>('');
  const [targetFitFilter, setTargetFitFilter] = useState<string>('');

  const [showFormModal, setShowFormModal] = useState(false);
  const [formState, setFormState] = useState<EditableFormState>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(null);

  const {
    data: targetAccountsRaw,
    isLoading,
    isFetching,
    error: targetAccountsError,
    refetch,
  } = useGetTargetAccountsQuery({ status: statusFilter });
  const targetAccounts: TargetAccount[] = useMemo(
    () => (Array.isArray(targetAccountsRaw) ? (targetAccountsRaw as TargetAccount[]) : []),
    [targetAccountsRaw]
  );

  const { data: clientsRaw } = useGetClientsQuery({});
  const clients: any[] = useMemo(
    () => (Array.isArray(clientsRaw) ? clientsRaw : []),
    [clientsRaw]
  );

  const { data: usersData } = useGetUsersQuery(undefined);
  const users: any[] = useMemo(() => {
    const arr = (usersData as any)?.data?.users;
    return Array.isArray(arr) ? arr : [];
  }, [usersData]);

  const [createTargetAccount, { isLoading: isCreating }] = useCreateTargetAccountMutation();
  const [updateTargetAccount, { isLoading: isUpdating }] = useUpdateTargetAccountMutation();
  const [deleteTargetAccount, { isLoading: isDeleting }] = useDeleteTargetAccountMutation();

  const isSaving = isCreating || isUpdating;

  const industries = useMemo(() => {
    const seen = new Set<string>();
    targetAccounts.forEach((ta) => {
      const ind = (ta.industry || '').trim();
      if (ind) seen.add(ind);
    });
    return Array.from(seen).sort((a, b) => a.localeCompare(b));
  }, [targetAccounts]);

  const visibleAccounts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return targetAccounts.filter((ta) => {
      if (industryFilter && (ta.industry || '') !== industryFilter) return false;
      if (ownerFilter && (ta.ownerId || '') !== ownerFilter) return false;
      if (stageFilter && (ta.stage || '') !== stageFilter) return false;
      if (targetFitFilter && (ta.targetFit || '') !== targetFitFilter) return false;
      if (!q) return true;
      const haystack = [
        ta.companyName,
        ta.website,
        ta.industry,
        ta.outreachAngle,
        ta.strategyNotes,
        ...(ta.tags || []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [targetAccounts, searchQuery, industryFilter, ownerFilter, stageFilter, targetFitFilter]);

  const ownerLookup = useMemo(() => {
    const map = new Map<string, string>();
    users.forEach((u) => {
      const id = u?.id || u?.userId || u?.sub;
      if (!id) return;
      const name = [u?.firstName, u?.lastName].filter(Boolean).join(' ').trim();
      map.set(id, name || u?.email || id);
    });
    return map;
  }, [users]);

  const clientLookup = useMemo(() => {
    const map = new Map<string, any>();
    clients.forEach((c) => {
      if (c?.id) map.set(c.id, c);
    });
    return map;
  }, [clients]);

  const openAddModal = () => {
    setFormError(null);
    setFormState(EMPTY_FORM);
    setShowFormModal(true);
  };

  const openEditModal = (ta: TargetAccount) => {
    setFormError(null);
    setFormState({
      id: ta.id,
      companyName: ta.companyName || '',
      website: ta.website || '',
      industry: ta.industry || '',
      locationCount: ta.locationCount || '',
      targetFit: (ta.targetFit as EditableFormState['targetFit']) || '',
      priority: (ta.priority as EditableFormState['priority']) || '',
      stage: (ta.stage as EditableFormState['stage']) || 'identified',
      outreachAngle: ta.outreachAngle || '',
      strategyNotes: ta.strategyNotes || '',
      linkedClientId: ta.linkedClientId || '',
      visibility: (ta.visibility as EditableFormState['visibility']) || 'global',
    });
    setShowFormModal(true);
  };

  const closeFormModal = () => {
    if (isSaving) return;
    setShowFormModal(false);
  };

  const handleFormChange = <K extends keyof EditableFormState>(field: K, value: EditableFormState[K]) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    const companyName = formState.companyName.trim();
    if (!companyName) {
      setFormError('Company name is required.');
      return;
    }

    const payload: any = {
      companyName,
      website: formState.website.trim(),
      industry: formState.industry.trim(),
      locationCount: formState.locationCount.trim(),
      targetFit: formState.targetFit || '',
      priority: formState.priority || '',
      stage: formState.stage,
      outreachAngle: formState.outreachAngle.trim(),
      strategyNotes: formState.strategyNotes.trim(),
      linkedClientId: formState.linkedClientId,
      visibility: formState.visibility,
    };

    try {
      if (formState.id) {
        await updateTargetAccount({ id: formState.id, ...payload }).unwrap();
      } else {
        await createTargetAccount(payload).unwrap();
      }
      setShowFormModal(false);
      setFormState(EMPTY_FORM);
    } catch (err: any) {
      const msg = err?.data?.error || err?.data?.message || err?.error || err?.message || 'Failed to save target account.';
      setFormError(String(msg));
    }
  };

  const handleArchiveToggle = async (ta: TargetAccount) => {
    const nextStatus = ta.status === 'archived' ? 'active' : 'archived';
    try {
      await updateTargetAccount({ id: ta.id, status: nextStatus }).unwrap();
    } catch (err) {
      console.error('Failed to toggle archive', err);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteTargetAccount(id).unwrap();
      setConfirmingDeleteId(null);
    } catch (err) {
      console.error('Failed to delete target account', err);
      setConfirmingDeleteId(null);
    }
  };

  const formatUpdated = (iso?: string) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return '—';
      return d.toLocaleDateString();
    } catch {
      return '—';
    }
  };

  const renderEmptyState = () => (
    <div className="text-center text-muted py-5">
      <Bullseye size={42} className="mb-3 opacity-50" />
      <div className="h5 mb-2">No target accounts yet</div>
      <div className="mb-3" style={{ maxWidth: 460, marginLeft: 'auto', marginRight: 'auto' }}>
        Capture the companies you want to pursue here, even ones you haven't talked to yet.
        Track strategy and outreach angle in one place before they become leads or deals.
      </div>
      <Button variant="primary" onClick={openAddModal}>
        <Plus className="me-1" /> Add your first target account
      </Button>
    </div>
  );

  const renderTable = () => (
    <Table responsive hover className="mb-0 crm-table activities-table" style={{ fontSize: '0.75rem' }}>
      <thead className="table-light">
        <tr>
          <th style={{ fontSize: '0.75rem' }}>Company</th>
          <th style={{ fontSize: '0.75rem' }}>Industry</th>
          <th style={{ fontSize: '0.75rem' }}>Locations</th>
          <th style={{ fontSize: '0.75rem' }}>Target Fit</th>
          <th style={{ fontSize: '0.75rem' }}>Priority</th>
          <th style={{ fontSize: '0.75rem' }}>Stage</th>
          <th style={{ fontSize: '0.75rem' }}>Owner</th>
          <th style={{ fontSize: '0.75rem' }}>Linked</th>
          <th style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Updated</th>
          <th style={{ width: 90, fontSize: '0.75rem' }} className="text-end">Actions</th>
        </tr>
      </thead>
      <tbody>
        {visibleAccounts.map((ta) => {
          const ownerName = ta.ownerId ? ownerLookup.get(ta.ownerId) || '—' : '—';
          const linkedClient = ta.linkedClientId ? clientLookup.get(ta.linkedClientId) : null;
          const websiteHref = normalizeWebsiteForHref(ta.website);
          const isArchived = ta.status === 'archived';
          return (
            <tr key={ta.id}>
              <td>
                <div className="fw-semibold">{ta.companyName}</div>
                {websiteHref ? (
                  <a
                    href={websiteHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-decoration-none d-inline-flex align-items-center"
                    style={{ fontSize: '0.7rem' }}
                  >
                    <Globe2 size={11} className="me-1" /> {ta.website}
                  </a>
                ) : null}
                {ta.outreachAngle ? (
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id={`angle-${ta.id}`}>Outreach angle: {ta.outreachAngle}</Tooltip>}
                  >
                    <div className="text-muted text-truncate" style={{ maxWidth: 260, fontSize: '0.7rem' }}>
                      {ta.outreachAngle}
                    </div>
                  </OverlayTrigger>
                ) : null}
              </td>
              <td>{ta.industry || '—'}</td>
              <td>{ta.locationCount || '—'}</td>
              <td>
                {ta.targetFit ? (
                  <Badge
                    bg={targetFitBadgeVariant(ta.targetFit)}
                    className="text-uppercase"
                    style={{ fontSize: '0.6rem', padding: '0.15rem 0.35rem' }}
                  >
                    {TARGET_FIT_LABELS[ta.targetFit]}
                  </Badge>
                ) : (
                  <span className="text-muted">—</span>
                )}
              </td>
              <td>
                {ta.priority ? (
                  <Badge
                    bg={priorityBadgeVariant(ta.priority)}
                    style={{ fontSize: '0.6rem', padding: '0.15rem 0.35rem' }}
                  >
                    {ta.priority}
                  </Badge>
                ) : (
                  <span className="text-muted">—</span>
                )}
              </td>
              <td>
                <Badge
                  bg={stageBadgeVariant(ta.stage)}
                  className="text-capitalize"
                  style={{ fontSize: '0.6rem', padding: '0.15rem 0.35rem' }}
                >
                  {STAGE_LABELS[ta.stage as EditableFormState['stage']] || ta.stage || '—'}
                </Badge>
              </td>
              <td>{ownerName}</td>
              <td>
                {linkedClient ? (
                  <span className="d-inline-flex align-items-center">
                    <Link45deg size={12} className="me-1 text-primary" />
                    {linkedClient.name || linkedClient.organization || linkedClient.id}
                  </span>
                ) : (
                  <span className="text-muted">—</span>
                )}
              </td>
              <td className="text-muted" style={{ whiteSpace: 'nowrap' }}>{formatUpdated(ta.updatedAt || ta.createdAt)}</td>
              <td className="text-end">
                <div className="d-inline-flex align-items-center">
                  <OverlayTrigger placement="top" overlay={<Tooltip id={`edit-${ta.id}`}>Edit</Tooltip>}>
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 me-2 text-secondary"
                      onClick={() => openEditModal(ta)}
                      aria-label="Edit target account"
                    >
                      <Pencil size={14} />
                    </Button>
                  </OverlayTrigger>
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id={`archive-${ta.id}`}>
                        {isArchived ? 'Restore (move to Active)' : 'Archive'}
                      </Tooltip>
                    }
                  >
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 me-2 text-secondary"
                      onClick={() => handleArchiveToggle(ta)}
                      aria-label={isArchived ? 'Restore target account' : 'Archive target account'}
                    >
                      {isArchived ? <ArrowCounterclockwise size={14} /> : <Archive size={14} />}
                    </Button>
                  </OverlayTrigger>
                  <OverlayTrigger placement="top" overlay={<Tooltip id={`delete-${ta.id}`}>Delete</Tooltip>}>
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 text-danger"
                      onClick={() => setConfirmingDeleteId(ta.id)}
                      aria-label="Delete target account"
                    >
                      <Trash size={14} />
                    </Button>
                  </OverlayTrigger>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );

  // Toggle the inbox/archive button group purely from the Status filter so it
  // stays in lockstep with the data the user is looking at (mirrors Leads).
  const mailboxView: 'inbox' | 'archive' = statusFilter === 'archived' ? 'archive' : 'inbox';
  const setMailboxView = (v: 'inbox' | 'archive') => setStatusFilter(v === 'archive' ? 'archived' : 'active');

  return (
    <Container fluid className="p-0" style={{ backgroundColor: '#ffffff', minHeight: '100vh' }}>
      {/* Page Header - matches Leads/Clients: title on left, global search on right */}
      <div style={{ backgroundColor: '#ffffff', paddingTop: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #dee2e6' }}>
        <Container fluid>
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <h5 className="mb-0" style={{ fontSize: '1.25rem', fontWeight: 500, color: '#212529' }}>Target Accounts</h5>
            </div>
            <div className="d-flex align-items-center gap-2">
              <InputGroup size="sm" style={{ width: '200px' }}>
                <InputGroup.Text style={{ backgroundColor: 'white', borderRight: 'none', fontSize: '0.75rem' }}>
                  <Search size={14} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search Kross Global"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{ borderLeft: 'none', fontSize: '0.75rem' }}
                  size="sm"
                />
              </InputGroup>
            </div>
          </div>
        </Container>
      </div>

      {/* Main Content */}
      <Container fluid className="py-3">
        {/* Action Bar (Inbox/Archive toggle + Add) on the left, filters on the right */}
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2">
            <div className="d-flex align-items-center">
              <OverlayTrigger placement="bottom" overlay={<Tooltip id="ta-inbox-view-tooltip">Active</Tooltip>}>
                <Button
                  variant={mailboxView === 'inbox' ? 'outline-primary' : 'outline-secondary'}
                  size="sm"
                  onClick={() => setMailboxView('inbox')}
                  style={{
                    width: '40px',
                    height: '34px',
                    padding: 0,
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Inbox size={14} />
                </Button>
              </OverlayTrigger>
              <OverlayTrigger placement="bottom" overlay={<Tooltip id="ta-archive-view-tooltip">Archived</Tooltip>}>
                <Button
                  variant={mailboxView === 'archive' ? 'outline-primary' : 'outline-secondary'}
                  size="sm"
                  onClick={() => setMailboxView('archive')}
                  style={{
                    width: '40px',
                    height: '34px',
                    padding: 0,
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    marginLeft: '-1px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Archive size={14} />
                </Button>
              </OverlayTrigger>
            </div>
            <Button
              variant="primary"
              size="sm"
              className="d-flex align-items-center"
              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
              onClick={openAddModal}
            >
              <Plus className="me-1" size={14} />
              Target Account
            </Button>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span className="text-muted small">{visibleAccounts.length} {visibleAccounts.length === 1 ? 'account' : 'accounts'}</span>
            <Form.Select
              size="sm"
              value={stageFilter}
              onChange={(e) => setStageFilter(e.target.value)}
              style={{ fontSize: '0.75rem', width: 'auto' }}
              aria-label="Filter by stage"
            >
              <option value="">Stage: All</option>
              {Object.entries(STAGE_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </Form.Select>
            <Form.Select
              size="sm"
              value={targetFitFilter}
              onChange={(e) => setTargetFitFilter(e.target.value)}
              style={{ fontSize: '0.75rem', width: 'auto' }}
              aria-label="Filter by target fit"
            >
              <option value="">Fit: Any</option>
              <option value="high">Fit: High</option>
              <option value="medium">Fit: Medium</option>
              <option value="low">Fit: Low</option>
            </Form.Select>
            <Form.Select
              size="sm"
              value={industryFilter}
              onChange={(e) => setIndustryFilter(e.target.value)}
              style={{ fontSize: '0.75rem', width: 'auto' }}
              aria-label="Filter by industry"
            >
              <option value="">All industries</option>
              {industries.map((ind) => (
                <option key={ind} value={ind}>{ind}</option>
              ))}
            </Form.Select>
            <Form.Select
              size="sm"
              value={ownerFilter}
              onChange={(e) => setOwnerFilter(e.target.value)}
              style={{ fontSize: '0.75rem', width: 'auto' }}
              aria-label="Filter by owner"
            >
              <option value="">All owners</option>
              {Array.from(ownerLookup.entries()).map(([id, name]) => (
                <option key={id} value={id}>{name}</option>
              ))}
            </Form.Select>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => refetch()}
              disabled={isFetching}
              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
            >
              {isFetching ? <Spinner size="sm" animation="border" /> : 'Refresh'}
            </Button>
          </div>
        </div>

        {targetAccountsError ? (
          <Alert variant="danger" className="mb-3">
            Failed to load target accounts. {(targetAccountsError as any)?.data?.error || (targetAccountsError as any)?.error || ''}
          </Alert>
        ) : null}

        <Card className="crm-table-card">
          <Card.Body className="p-0">
            {isLoading ? (
              <div className="text-center py-5">
                <Spinner animation="border" />
              </div>
            ) : visibleAccounts.length === 0 ? (
              targetAccounts.length === 0 ? renderEmptyState() : (
                <div className="text-center text-muted py-5" style={{ fontSize: '0.75rem' }}>
                  <div className="mb-2">No target accounts match the current filters.</div>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => {
                      setSearchQuery('');
                      setIndustryFilter('');
                      setOwnerFilter('');
                      setStageFilter('');
                      setTargetFitFilter('');
                    }}
                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                  >
                    Clear filters
                  </Button>
                </div>
              )
            ) : (
              renderTable()
            )}
          </Card.Body>
        </Card>
      </Container>

      {/* Add/Edit modal */}
      <Modal show={showFormModal} onHide={closeFormModal} size="lg" backdrop="static">
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              {formState.id ? 'Edit Target Account' : 'Add Target Account'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {formError ? <Alert variant="danger">{formError}</Alert> : null}
            <Row className="g-3">
              <Col xs={12} md={8}>
                <Form.Group>
                  <Form.Label>Company name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    value={formState.companyName}
                    onChange={(e) => handleFormChange('companyName', e.target.value)}
                    autoFocus
                    required
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={4}>
                <Form.Group>
                  <Form.Label>Visibility</Form.Label>
                  <Form.Select
                    value={formState.visibility}
                    onChange={(e) => handleFormChange('visibility', e.target.value as 'global' | 'personal')}
                  >
                    <option value="global">Shared with team</option>
                    <option value="personal">Just me</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label>Website</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="example.com"
                    value={formState.website}
                    onChange={(e) => handleFormChange('website', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label>Industry / category</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. Retail, Healthcare"
                    value={formState.industry}
                    onChange={(e) => handleFormChange('industry', e.target.value)}
                  />
                </Form.Group>
              </Col>

              <Col xs={12} md={4}>
                <Form.Group>
                  <Form.Label>Location count / range</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder='e.g. "30-80", "50+", "12"'
                    value={formState.locationCount}
                    onChange={(e) => handleFormChange('locationCount', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={6} md={4}>
                <Form.Group>
                  <Form.Label>Target fit</Form.Label>
                  <Form.Select
                    value={formState.targetFit}
                    onChange={(e) => handleFormChange('targetFit', e.target.value as EditableFormState['targetFit'])}
                  >
                    <option value="">—</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col xs={6} md={4}>
                <Form.Group>
                  <Form.Label>Priority</Form.Label>
                  <Form.Select
                    value={formState.priority}
                    onChange={(e) => handleFormChange('priority', e.target.value as EditableFormState['priority'])}
                  >
                    <option value="">—</option>
                    <option value="P1">P1 — High</option>
                    <option value="P2">P2 — Medium</option>
                    <option value="P3">P3 — Low</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label>Stage</Form.Label>
                  <Form.Select
                    value={formState.stage}
                    onChange={(e) => handleFormChange('stage', e.target.value as EditableFormState['stage'])}
                  >
                    {Object.entries(STAGE_LABELS).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label>Link to existing Client (optional)</Form.Label>
                  <Form.Select
                    value={formState.linkedClientId}
                    onChange={(e) => handleFormChange('linkedClientId', e.target.value)}
                  >
                    <option value="">— Not linked —</option>
                    {clients.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name || c.organization || c.email || c.id}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    If this company already exists as a Client in CRM, link it here instead of duplicating.
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col xs={12}>
                <Form.Group>
                  <Form.Label>Current outreach angle</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="What's the pitch / angle we're pursuing right now?"
                    value={formState.outreachAngle}
                    onChange={(e) => handleFormChange('outreachAngle', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group>
                  <Form.Label>Strategy notes</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    placeholder="Why this company? Decision-makers, signals, timing, blockers, next steps…"
                    value={formState.strategyNotes}
                    onChange={(e) => handleFormChange('strategyNotes', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={closeFormModal} disabled={isSaving}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={isSaving}>
              {isSaving ? <Spinner size="sm" animation="border" className="me-2" /> : null}
              {formState.id ? 'Save changes' : 'Add Target Account'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete confirmation */}
      <Modal show={!!confirmingDeleteId} onHide={() => setConfirmingDeleteId(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete target account?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            This will permanently remove the target account. Linked Clients / Accounts / Leads will be unaffected.
          </p>
          <p className="text-muted small mb-0">
            If you just want to hide it from the main view, archive it instead.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setConfirmingDeleteId(null)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => confirmingDeleteId && handleDelete(confirmingDeleteId)}
            disabled={isDeleting || !confirmingDeleteId}
          >
            {isDeleting ? <Spinner size="sm" animation="border" className="me-2" /> : null}
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default TargetAccounts;
